import _ from 'lodash';
import qs from 'qs';
import history from 'common/history';
import api from '../../qa/api';
import { createQueryString } from '../utils/queryStringUtils';
import apolloClient from '../utils/apolloClient';
import CREATE_CALIBRATION_AUDIT from 'common/graphql/mutations/CREATE_CALIBRATION_AUDIT';

// AssignmentActions
const FETCH_ALL_ASSIGNMENTS_SUCCESS =
  'qaAssignments/FETCH_ALL_ASSIGNMENTS_SUCCESS';
const FETCH_ALL_ASSIGNMENTS_REQUEST =
  'qaAssignments/FETCH_ALL_ASSIGNMENTS_REQUEST';
const FETCH_MY_ASSIGNMENTS_SUCCESS =
  'qaAssignments/FETCH_MY_ASSIGNMENTS_SUCCESS';
const FETCH_MY_ASSIGNMENTS_REQUEST =
  'qaAssignments/FETCH_MY_ASSIGNMENTS_REQUEST';
const REMOVE_ASSIGNMENT = 'qaAssignments/REMOVE_ASSIGNMENT';
const UPDATE_CONFETTI = 'qaAssignments/UPDATE_CONFETTI';

// Reducer

export const initialState = {
  pages: 0,
  qaAssignments: [],
  currentUserQaAssignments: [],
  loadingMyAssignments: false,
  loadingAllAssignments: false,
  hideConfetti: true,
};

export function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_ALL_ASSIGNMENTS_REQUEST: {
      return { ...state, loadingAllAssignments: true };
    }
    case FETCH_MY_ASSIGNMENTS_REQUEST: {
      return { ...state, loadingMyAssignments: true };
    }
    case REMOVE_ASSIGNMENT: {
      const qaAssignments = _.filter(
        state.qaAssignments,
        a => a.id !== action.id
      );
      const currentUserQaAssignments = _.filter(
        state.currentUserQaAssignments,
        a => a.id !== action.id
      );
      return { ...state, qaAssignments, currentUserQaAssignments };
    }
    case FETCH_ALL_ASSIGNMENTS_SUCCESS: {
      return {
        ...state,
        qaAssignments: action.qaAssignments,
        pages: action.pages,
        loadingAllAssignments: false,
      };
    }
    case FETCH_MY_ASSIGNMENTS_SUCCESS: {
      return {
        ...state,
        currentUserQaAssignments: action.currentUserQaAssignments,
        loadingMyAssignments: false,
      };
    }
    case UPDATE_CONFETTI: {
      return { ...state, hideConfetti: action.bool };
    }
    default: {
      return state;
    }
  }
}

// Action creators

export const updateConfetti = bool => ({
  type: UPDATE_CONFETTI,
  bool,
});

export const removeAssignment = id => ({
  type: REMOVE_ASSIGNMENT,
  id,
});

export const fetchAllAssignmentsRequest = () => ({
  type: FETCH_ALL_ASSIGNMENTS_REQUEST,
});

export const fetchMyAssignmentsRequest = () => ({
  type: FETCH_MY_ASSIGNMENTS_REQUEST,
});

const fetchAllAssignmentsSuccess = ({ qaAssignments, pages }) => ({
  type: FETCH_ALL_ASSIGNMENTS_SUCCESS,
  qaAssignments,
  pages,
});
const fetchMyAssignmentsSuccess = ({ currentUserQaAssignments }) => ({
  type: FETCH_MY_ASSIGNMENTS_SUCCESS,
  currentUserQaAssignments,
});

// Thunks

export const getNextReviewUrl = (dispatch, assignmentId, reviewSimilar, defaultFormId, employeeId) => {
  const params = {
    employee_id: employeeId || undefined,
    review_similar: reviewSimilar || undefined,
  };
  const queryString = createQueryString(params);
  return api
    .fetch(
      dispatch,
      `qa_assignments/${assignmentId}/next_review${queryString ? `?${queryString}` : ''}`
    )
    .then(([json]) => {
      const isCalibrationAudit = json.qa_review_type === 'qa_calibration_audit';

      if (isCalibrationAudit) {
        if (!json.qa_review_id) {
          return '';
        }
        return apolloClient.mutate({
          mutation: CREATE_CALIBRATION_AUDIT,
          variables: {
            input: {
              qaReviewId: json.qa_review_id,
              qaAssignmentId: assignmentId,
              qaAllocationId: json.qa_allocation_id,
            },
          },
        }).then(response => {
          const auditId = response.data.createCalibrationAudit.id;
          return `/qa/audit/${auditId}`;
        })
      } else if (!isCalibrationAudit && json.uuid && json.qa_allocation_id) {
        const queryParamWithPrefix = queryString ? `&${queryString}` : '';
        const url = `/qa/reviews/new?solicitation_id=${json.uuid}&qa_allocation_id=${json.qa_allocation_id}&qa_assignment_id=${json.qa_assignment_id}&blind=${json.blind}${queryParamWithPrefix}`;
        return defaultFormId
          ? `${url}&default_qa_form_id=${defaultFormId}`
          : url;
      }
      return '';
    });
};

export const startReview =
  (
    qaAssignmentId,
    defaultFormId = null,
    employeeId = null,
    reviewSimilar = false
  ) =>
    dispatch => {
      return getNextReviewUrl(dispatch, qaAssignmentId, reviewSimilar, defaultFormId, employeeId)
        .then(nextReviewUrl => {
          if (nextReviewUrl) {
            const withEmployee = employeeId ? `&employee_id=${employeeId}` : '';
            history.push(`${nextReviewUrl}${withEmployee}`);
            return true;
          }

          // Other flows might want to handle the "no interactions" case separately
          if (window.location.pathname === '/qa/assignments' && !employeeId) {
            history.push(`/qa/assignments/${qaAssignmentId}/review_similar`);
          }
          return false;
        });
    };

export const fetchAllAssignments =
  (startDate, endDate, archived = false, page = 1) =>
    dispatch => {
      const params = {
        archived,
        start_date: startDate,
        end_date: endDate,
        page,
        per: 10,
      };
      const stringParams = qs.stringify(params);

      dispatch(fetchAllAssignmentsRequest());
      return api
        .fetch(dispatch, `qa_assignments?${stringParams}`)
        .then(([json]) => {
          const qaAssignments = json.qa_assignments;
          const { pages } = json;
          dispatch(fetchAllAssignmentsSuccess({ qaAssignments, pages }));
        });
    };

export const fetchMyAssignments =
  (startDate, endDate, archived = false) =>
    dispatch => {
      const params = {
        archived,
        my_assignments: true,
        start_date: startDate,
        end_date: endDate,
      };
      const stringParams = qs.stringify(params);
      dispatch(fetchMyAssignmentsRequest());
      return api
        .fetch(dispatch, `qa_assignments?${stringParams}`)
        .then(([json]) => {
          const {
            pages,
            qa_assignments: qaAssignments,
            current_user_id: currentUserId,
          } = json;
          const currentUserQaAssignments = qaAssignments.map(assignment => {
            const currentUserProgress = assignment.progress_by_reviewer.find(
              data => data.employee?.id === currentUserId
            );
            return { ...assignment, ...currentUserProgress };
          });
          dispatch(
            fetchMyAssignmentsSuccess({ currentUserQaAssignments, pages })
          );
        });
    };
